/*
 * Shared offline/fallback search logic for the Capucinne AI consultant.
 *
 * Used by BOTH the global header search modal (capucinne-homepage.js) and the
 * consultant page (capucinne-consultant-page.js) so the two surfaces behave
 * identically when the AI backend is unavailable or returns a fallback reply:
 * derive search keywords from the shopper's message and surface real storefront
 * products instead of looping on a canned "tell me your budget" question.
 *
 * This module owns only the surface-independent logic (query parsing, the
 * storefront product lookup, and price formatting). Each surface keeps its own
 * card rendering because the markup/CSS hooks differ.
 */

/*
 * Shared analytics bridge for the AI consultant + search. Emits each engagement
 * event to the GTM dataLayer (`{event:"capucinne_ai_<name>", ...}`), to gtag/GA4
 * if a page-level gtag exists, and to a DOM CustomEvent (`capucinne:ai-event`).
 * Used by BOTH the consultant page and the header search modal so per-session
 * engagement (opens, prompts, messages, product clicks, voice) is measured the
 * same way on each surface. Never throws if no analytics is present.
 */
window.CapucinneAITrack = window.CapucinneAITrack || (function () {
  function track(name, params) {
    var detail = { event: 'capucinne_ai_' + name };
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) detail[key] = params[key];
      }
    }
    try { (window.dataLayer = window.dataLayer || []).push(detail); } catch (error) { /* no GTM */ }
    try { if (typeof window.gtag === 'function') window.gtag('event', detail.event, detail); } catch (error) { /* no gtag */ }
    try { document.dispatchEvent(new CustomEvent('capucinne:ai-event', { detail: detail })); } catch (error) { /* no CustomEvent */ }
  }
  return { track: track };
})();

window.CapucinneAISearch = window.CapucinneAISearch || (function () {
  // Per-conversation memory so a bare refinement ("Under $2,000") can reuse the
  // previous product query instead of producing an empty search.
  function createState() {
    return { lastQuery: '' };
  }

  // Turn a conversational request into a concise catalog query. This value is
  // also used in regular-search fallback URLs, so never return the shopper's
  // full sentence or an informational/policy question.
  function deriveQuery(message, state) {
    state = state || {};

    var source = String(message || '').toLowerCase();
    var hasProductWords = /\b(?:jewel(?:ry|lery)|ring|rings|band|bands|necklace|necklaces|pendant|pendants|earring|earrings|bracelet|bracelets|stone|stones|gemstone|gemstones|diamond|diamonds|sapphire|sapphires|emerald|emeralds|moissanite|agate)\b/.test(source);
    // Real shopping vocabulary beyond a bare "ring"/"band" mention: stones,
    // metals, occasions, categories, or an explicit browse/buy verb. A policy
    // question that happens to mention the shopper's own ring ("can I resize
    // my ring later?") has none of these and must NOT become a store search.
    var hasShoppingSignal = /\b(?:engagement|wedding|bridal|eternity|stacking|nesting|solitaire|halo|sapphire|sapphires|diamond|diamonds|emerald|emeralds|moissanite|opal|opals|moonstone|ruby|rubies|topaz|garnet|amethyst|aquamarine|morganite|alexandrite|tourmaline|tanzanite|peridot|citrine|pearl|pearls|onyx|agate|quartz|gold|platinum|silver|necklace|necklaces|pendant|pendants|earring|earrings|bracelet|bracelets|buy|shop|browse|find|show|recommend|suggest|looking)\b/.test(source);
    var isInformationOnly = /\b(?:ship|ships|shipped|shipping|deliver|delivery|deliveries|delivered|return|returns|refund|refunds|exchange|exchanges|cancel|warranty|policy|policies|contact|payment|payments|financing|care|clean|cleaning|resize|resizing|timeline)\b/.test(source) && !hasShoppingSignal;

    if (isInformationOnly) return '';

    var raw = ' ' + source + ' ';
    raw = raw.replace(/\$\s?\d[\d,.]*/g, ' ');
    raw = raw.replace(/\b\d[\d,.]*\s*(usd|eur|gbp|cad|aud|rsd|dollars?|euros?|pounds?|dinars?|k)\b/g, ' ');
    raw = raw.replace(/\b\d[\d,.]{2,}\b/g, ' ');

    var stop = {
      find: 1, me: 1, show: 1, please: 1, i: 1, want: 1, wanted: 1, looking: 1, look: 1,
      for: 1, under: 1, below: 1, over: 1, above: 1, around: 1, about: 1, between: 1,
      budget: 1, price: 1, priced: 1, cost: 1, costs: 1, max: 1, maximum: 1, min: 1,
      usd: 1, eur: 1, gbp: 1, cad: 1, aud: 1, rsd: 1, dollar: 1, dollars: 1, euro: 1, euros: 1, pound: 1, pounds: 1, dinar: 1, dinars: 1,
      my: 1, need: 1, get: 1, would: 1, like: 1, love: 1, a: 1, an: 1, the: 1, with: 1,
      to: 1, of: 1, in: 1, on: 1, is: 1, are: 1, can: 1, you: 1, help: 1, something: 1,
      anything: 1, some: 1, that: 1, this: 1, and: 1, or: 1, thanks: 1, have: 1,
      has: 1, had: 1, do: 1, does: 1, did: 1, options: 1, option: 1, share: 1,
      detail: 1, details: 1, what: 1, whats: 1, who: 1, it: 1, your: 1, yourself: 1,
      how: 1, when: 1, where: 1, why: 1, which: 1, clean: 1, ship: 1, deliver: 1,
      partner: 1, recipient: 1, preference: 1, preferences: 1, type: 1, color: 1,
      relevant: 1, catalog: 1, product: 1, products: 1, browse: 1, within: 1, flexible: 1,
      shipping: 1, delivery: 1, return: 1, returns: 1, refund: 1, refunds: 1,
      warranty: 1, policy: 1, policies: 1, contact: 1, payment: 1, payments: 1,
      financing: 1, care: 1, cleaning: 1, resize: 1, resizing: 1, timeline: 1,
      // Conversational filler that otherwise survives into the derived query
      // and reads as nonsense in a search link ("ring later", "ring again").
      later: 1, soon: 1, now: 1, today: 1, tomorrow: 1, yesterday: 1, again: 1,
      once: 1, ever: 1, still: 1, also: 1, just: 1, really: 1, very: 1,
      actually: 1, maybe: 1, perhaps: 1, possibly: 1, possible: 1, already: 1,
      yet: 1, sometime: 1, someday: 1, eventually: 1
    };

    var tokens = raw.split(/[^a-z]+/).filter(function (token) {
      return token && token.length > 1 && !stop[token];
    });

    var cleaned = tokens.join(' ').trim();

    if (/\bgifts?\b/.test(source) && !hasProductWords) {
      cleaned = 'jewelry gift';
    }

    if (cleaned.length < 2) {
      if (state.lastQuery) return state.lastQuery;
      return '';
    }

    state.lastQuery = cleaned;
    return cleaned;
  }

  function formatMoney(amount, locale, currency) {
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return '$' + Number(amount).toLocaleString();
    }
  }

  function formatPrice(value, locale, currency) {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'string') {
      var trimmed = value.trim();
      if (!trimmed) return '';
      // Already money-formatted (carries a symbol or letters) — use as-is.
      if (/[^\d.,\s]/.test(trimmed)) return trimmed;
      var parsed = parseFloat(trimmed.replace(/,/g, ''));
      return isFinite(parsed) ? formatMoney(parsed, locale, currency) : trimmed;
    }

    if (typeof value === 'number' && isFinite(value)) return formatMoney(value, locale, currency);
    return '';
  }

  function numericPrice(value) {
    if (typeof value === 'number' && isFinite(value)) return value;
    if (value && typeof value === 'object') {
      return numericPrice(value.amount || value.value || value.price || value.min || value.minVariantPrice);
    }

    var match = String(value || '').match(/([0-9]{1,3}(?:,[0-9]{3})+(?:\.\d{1,2})?|[0-9]{4,}(?:\.\d{1,2})?|[0-9]+(?:\.\d{1,2})\s*k)/i);
    if (!match) return NaN;
    var token = match[1].replace(/\s+/g, '');
    var multiplier = /k$/i.test(token) ? 1000 : 1;
    var parsed = parseFloat(token.replace(/k$/i, '').replace(/,/g, ''));
    return isFinite(parsed) ? parsed * multiplier : NaN;
  }

  function normalizeImage(value) {
    if (!value) return '';

    if (typeof value === 'string') {
      var imageUrl = value.trim();
      if (imageUrl.indexOf('//') === 0) return window.location.protocol + imageUrl;
      return imageUrl;
    }

    if (Array.isArray(value)) {
      return value.length ? normalizeImage(value[0]) : '';
    }

    if (typeof value === 'object') {
      return normalizeImage(
        value.url ||
        value.src ||
        value.originalSrc ||
        value.transformedSrc ||
        value.image ||
        value.preview_image ||
        value.previewImage ||
        (value.node && value.node.image) ||
        (value.node && value.node.url)
      );
    }

    return '';
  }

  function firstConnectionImage(value) {
    if (!value || typeof value !== 'object') return '';

    if (Array.isArray(value)) return normalizeImage(value);
    if (Array.isArray(value.nodes)) return normalizeImage(value.nodes);
    if (Array.isArray(value.edges)) return normalizeImage(value.edges);
    return normalizeImage(value);
  }

  function normalizeProduct(item, locale, currency) {
    item = item && typeof item === 'object' ? item : {};

    var url = String(item.url || item.product_url || item.productUrl || item.link || item.handle || '').trim();
    if (url && url.indexOf('http') !== 0 && url.indexOf('/') !== 0) url = '/products/' + url;

    var image = normalizeImage(
      item.image ||
      item.image_url ||
      item.imageUrl ||
      item.featured_image ||
      item.featuredImage ||
      item.thumbnail ||
      item.preview_image
    ) || firstConnectionImage(item.images) || firstConnectionImage(item.media);

    var rawPrice = item.price || item.price_text || item.priceText || item.formatted_price || item.display_price;
    if (!rawPrice && item.price_range) rawPrice = item.price_range.min || item.price_range.minVariantPrice;
    if (!rawPrice && item.priceRange) rawPrice = item.priceRange.min || item.priceRange.minVariantPrice;

    if (rawPrice && typeof rawPrice === 'object') {
      rawPrice = rawPrice.amount || rawPrice.value || rawPrice.price || '';
    }

    return {
      title: String(item.title || item.name || item.product_title || item.productTitle || '').trim(),
      url: url,
      image: image,
      price: formatPrice(rawPrice, locale, currency),
      priceValue: numericPrice(rawPrice),
      label: String(item.label || item.product_type || item.productType || item.collection || item.vendor || '').trim(),
      id: String(item.id || item.product_id || item.productId || '').trim(),
      matchText: [
        item.title,
        item.name,
        item.product_title,
        item.label,
        item.product_type,
        item.productType,
        item.collection,
        item.vendor,
        item.tags && (Array.isArray(item.tags) ? item.tags.join(' ') : item.tags),
        item.description,
        item.body,
        item.matchText
      ].filter(Boolean).join(' ').toLowerCase()
    };
  }

  function hydrateProduct(item, locale, currency) {
    var normalized = normalizeProduct(item, locale, currency);
    if (!normalized.url) return Promise.resolve(normalized);

    var productUrl;

    try {
      productUrl = new URL(normalized.url, window.location.origin);
    } catch (error) {
      return Promise.resolve(normalized);
    }

    if (productUrl.pathname.indexOf('/products/') === -1) {
      return Promise.resolve(normalized);
    }

    var jsonPath = productUrl.pathname.replace(/\/$/, '') + '.js';

    return fetch(jsonPath, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('product ' + response.status);
        return response.json();
      })
      .then(function (product) {
        var hydrated = normalizeProduct({
          title: normalized.title || product.title,
          url: normalized.url,
          image: product.featured_image || product.images || normalized.image,
          price: typeof product.price === 'number' ? product.price / 100 : (product.price || normalized.price),
          label: normalized.label || product.type || product.vendor,
          id: normalized.id || product.id,
          tags: product.tags,
          description: product.description || product.body_html,
          matchText: normalized.matchText
        }, locale, currency);

        return hydrated;
      })
      .catch(function () {
        return normalized;
      });
  }

  // Look up matching products via Shopify predictive search and normalise each
  // into the card shape both surfaces render: { title, url, image, price, label }.
  function fetchProducts(query, locale, currency) {
    var params = 'q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';

    return fetch('/search/suggest.json?' + params, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('suggest ' + response.status);
        return response.json();
      })
      .then(function (json) {
        var list = json && json.resources && json.resources.results && json.resources.results.products;
        if (!Array.isArray(list)) return [];

        return list
          .filter(function (product) {
            return product && !(Array.isArray(product.tags) && product.tags.indexOf('hidden') !== -1);
          })
          .map(function (product) {
            return normalizeProduct(product, locale, currency);
          });
      });
  }

  function intro() {
    return 'Here are a few pieces from our collection that match what you described. Tap any to see full details and pricing, or tell me a stone, metal, or budget and I will refine.';
  }

  function cleanAnswer(value) {
    var text = String(value || '');
    // Drop a ```json ...``` fenced block the model sometimes appends.
    text = text.replace(/```json[\s\S]*$/i, '');
    // Drop a trailing structured-JSON object that leaked into the answer text,
    // e.g. {"quick_replies":[...]} or {"answer":"...","search_query":"..."} —
    // these are control fields, not prose, and must never render to the shopper.
    text = text.replace(/\{\s*"(?:answer|quick_replies|search_query|recommendations)"[\s\S]*\}\s*$/i, '');
    return text.trim();
  }

  function noMatch() {
    return 'I could not find a direct match for that just yet. Try a different stone, cut, or metal, or browse the full collection below.';
  }

  function filterProducts(products, contextText) {
    if (!Array.isArray(products)) return [];
    var constraints = extractClientConstraints(contextText);

    return products.filter(function (product) {
      var normalized = product && typeof product === 'object' ? product : {};

      if (isFinite(constraints.maxPrice)) {
        var price = isFinite(normalized.priceValue) ? normalized.priceValue : numericPrice(normalized.price);
        if (!isFinite(price) || price > constraints.maxPrice) return false;
      }

      if (constraints.labGrown) {
        var evidence = String(normalized.matchText || [normalized.title, normalized.label].join(' ')).toLowerCase();
        if (!/\b(?:lab(?:oratory)?[-\s]?(?:grown|created)|lab[-\s]?(?:diamond|gemstone)|synthetic\s+diamond)\b/i.test(evidence)) return false;
      }

      return true;
    });
  }

  function extractClientConstraints(value) {
    var text = String(value || '');
    var budgetMatch = text.match(/(?:under|below|less\s+than|up\s+to|not\s+over|no\s+more\s+than|within|max(?:imum)?(?:\s+budget)?(?:\s+of)?|budget(?:\s+is|\s+of|\s*:)?|spend(?:ing)?(?:\s+is|\s+of|\s*:)?)[\s]*(?:USD|EUR|GBP)?\s*[$€£]?\s*([\d,.]+(?:\s*k)?)/i);
    var maxPrice = budgetMatch ? numericPrice(budgetMatch[1]) : NaN;

    return {
      maxPrice: maxPrice,
      labGrown: /\b(?:lab(?:oratory)?[-\s]?(?:grown|created)|lab[-\s]?diamond|synthetic\s+diamond)\b/i.test(text)
    };
  }

  function constraintNoMatch(contextText) {
    var constraints = extractClientConstraints(contextText);
    if (constraints.labGrown || isFinite(constraints.maxPrice)) {
      var details = [];
      if (constraints.labGrown) details.push('lab-grown');
      if (isFinite(constraints.maxPrice)) details.push('at or below $' + constraints.maxPrice.toLocaleString());
      return 'I wasn\'t able to find a piece that is ' + details.join(' and ') + ' just now — and I\'d never quietly show you a higher-priced or different piece. Shall we adjust one detail?';
    }
    return noMatch();
  }

  return {
    createState: createState,
    cleanAnswer: cleanAnswer,
    deriveQuery: deriveQuery,
    filterProducts: filterProducts,
    fetchProducts: fetchProducts,
    formatPrice: formatPrice,
    numericPrice: numericPrice,
    normalizeProduct: normalizeProduct,
    hydrateProduct: hydrateProduct,
    intro: intro,
    noMatch: noMatch,
    constraintNoMatch: constraintNoMatch
  };
})();

window.CapucinneAIConversation = window.CapucinneAIConversation || (function () {
  var COOKIE_NAME = 'capucinne_ai_session';
  // Persist the conversation across page loads AND return visits (previously a
  // session cookie that reset on browser close, fragmenting one shopper into
  // many dashboard conversations). 90 days keeps a returning shopper's thread.
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
  var MAX_COOKIE_BYTES = 3600;
  var STORAGE_KEY = COOKIE_NAME;
  // localStorage holds the full thread. Cookies cap at ~4KB and a single turn's
  // product cards already blow past that, which is why the chat kept vanishing.
  var MAX_STORE_BYTES = 200000;
  // The whole thread survives a page load (audit 2026-10-20: 10 entries of 500
  // characters made the consultant forget the start of a chat after a product
  // page visit and ask the occasion and budget again, #1066). The byte cap in
  // save() still drops the oldest turns first.
  var MAX_HISTORY_ITEMS = 400;
  var MAX_MESSAGE_CHARS = 4000;

  function load() {
    var encoded = '';
    try { encoded = window.localStorage.getItem(STORAGE_KEY) || ''; } catch (error) { encoded = ''; }
    // Migrate from the legacy cookie (and keep working if localStorage is off).
    if (!encoded) encoded = readCookie(COOKIE_NAME);
    if (!encoded) return emptySession();

    try {
      var parsed = JSON.parse(decodeURIComponent(encoded));
      return normalizeSession(parsed);
    } catch (error) {
      return emptySession();
    }
  }

  function save(value) {
    var session = normalizeSession(value);
    var encoded = encodeSession(session);

    // Ample room in localStorage — trim only as a backstop.
    while (encoded.length > MAX_STORE_BYTES && session.history.length > 0) {
      session.history.shift();
      encoded = encodeSession(session);
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, encoded);
      // Drop the legacy size-limited cookie now that localStorage holds the thread.
      document.cookie = COOKIE_NAME + '=; Path=/; SameSite=Lax; Max-Age=0' +
        (window.location && window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (error) {
      // localStorage unavailable/full: fall back to a trimmed cookie.
      var slim = encodeSession(session);
      while (slim.length > MAX_COOKIE_BYTES && session.history.length > 0) {
        session.history.shift();
        slim = encodeSession(session);
      }
      document.cookie = COOKIE_NAME + '=' + slim + '; Path=/; Max-Age=' + COOKIE_MAX_AGE + '; SameSite=Lax' +
        (window.location && window.location.protocol === 'https:' ? '; Secure' : '');
    }
    return session;
  }

  function clear() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (error) { /* ignore */ }
    document.cookie = COOKIE_NAME + '=; Path=/; SameSite=Lax; Max-Age=0' +
      (window.location && window.location.protocol === 'https:' ? '; Secure' : '');
  }

  function normalizeSession(value) {
    var source = value && typeof value === 'object' ? value : {};
    var rawHistory = Array.isArray(source.history) ? source.history.slice(-MAX_HISTORY_ITEMS) : [];
    var history = rawHistory.map(function (entry) {
      var role = entry && entry.role === 'assistant' ? 'assistant' : 'user';
      var content = String(entry && (entry.content || entry.message) || '').trim().slice(0, MAX_MESSAGE_CHARS);
      if (!content) return null;
      var item = { role: role, content: content };
      // When the turn happened: the consultant reads a long pause as a new
      // request, so an old budget never carries into it (#620, 2026-10-20).
      var at = Number(entry && entry.at);
      if (at > 0) item.at = at;
      // Preserve the product cards shown with an assistant turn so they can be
      // re-rendered when the shared session is restored on another surface.
      // (The cookie size cap in save() drops oldest turns first if needed.)
      var recs = entry && entry.recommendations;
      if (role === 'assistant' && Array.isArray(recs) && recs.length) {
        var compact = recs.slice(0, 6).map(function (rec) {
          rec = rec && typeof rec === 'object' ? rec : {};
          var out = {
            url: String(rec.url || '').slice(0, 300),
            image: String(rec.image || '').slice(0, 300),
            title: String(rec.title || '').slice(0, 160),
            price: String(rec.price || '').slice(0, 40),
            label: String(rec.label || '').slice(0, 80)
          };
          // The metal the worker priced the card in ("platinum"), so a restored
          // card shows that metal's market price again (review B 2026-10-26).
          if (rec.price_metal) out.price_metal = String(rec.price_metal).slice(0, 20);
          return out;
        }).filter(function (rec) { return rec.url; });
        if (compact.length) item.recommendations = compact;
      }
      // ...and the "View all matching ..." link shown under them (merchant
      // 2026-09-22), so it is still there after the shopper comes back from a
      // product page.
      var browse = entry && entry.browse;
      if (role === 'assistant' && browse && typeof browse === 'object' && browse.url) {
        item.browse = { url: String(browse.url).slice(0, 400), label: String(browse.label || '').slice(0, 160) };
      }
      // ...and the worker's link buttons under the reply - the free video
      // call, contact, custom design, size guide (audit ISS-01, 2026-09-28).
      // The page re-checks each link's host before it shows one.
      var links = entry && entry.links;
      if (role === 'assistant' && Array.isArray(links) && links.length) {
        var keptLinks = links.slice(0, 3).map(function (link) {
          link = link && typeof link === 'object' ? link : {};
          return {
            kind: String(link.kind || '').slice(0, 32),
            label: String(link.label || '').slice(0, 80),
            url: String(link.url || '').slice(0, 400)
          };
        }).filter(function (link) { return /^https:\/\//i.test(link.url); });
        if (keptLinks.length) item.links = keptLinks;
      }
      return item;
    }).filter(Boolean);

    return {
      version: 1,
      conversationId: String(source.conversationId || source.conversation_id || '').slice(0, 120),
      history: history,
      cartId: String(source.cartId || source.cart_id || '').slice(0, 500),
      checkoutUrl: String(source.checkoutUrl || source.checkout_url || '').slice(0, 1000),
      // A loaded thread keeps the time it was last saved; a save stamps now.
      updatedAt: Number(source.updatedAt) > 0 ? Number(source.updatedAt) : Date.now()
    };
  }

  function emptySession() {
    return {
      version: 1,
      conversationId: '',
      history: [],
      cartId: '',
      checkoutUrl: '',
      updatedAt: 0
    };
  }

  function encodeSession(session) {
    return encodeURIComponent(JSON.stringify(session));
  }

  function readCookie(name) {
    var prefix = name + '=';
    var parts = String(document.cookie || '').split(';');

    for (var index = 0; index < parts.length; index += 1) {
      var part = parts[index].trim();
      if (part.indexOf(prefix) === 0) return part.slice(prefix.length);
    }

    return '';
  }

  return {
    clear: clear,
    load: load,
    save: save
  };
})();

// Shared, dependency-free Markdown renderer for assistant messages. It handles
// the structured content Claude commonly returns (headings, rules, tables,
// lists, emphasis, links and images) and is safe to call repeatedly while a
// response is being revealed.
window.CapucinneAIMarkdown = window.CapucinneAIMarkdown || (function () {
  function render(container, value, classPrefix) {
    if (!container) return;

    var prefix = classPrefix || 'cap-ai-consultant';
    var lines = String(value || '').replace(/\r/g, '').split('\n');
    var index = 0;
    container.textContent = '';

    while (index < lines.length) {
      var line = lines[index];
      var trimmed = line.trim();

      if (!trimmed) {
        index += 1;
        continue;
      }

      var imageMatch = trimmed.match(/^!\[([^\]]*)\]\((https?:\/\/[^)]+)\)$/i);
      if (imageMatch) {
        appendImage(container, imageMatch[2], imageMatch[1], prefix);
        index += 1;
        continue;
      }

      var headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        var heading = document.createElement(headingMatch[1].length <= 2 ? 'h3' : 'h4');
        heading.className = prefix + '__answer-heading';
        appendInline(heading, headingMatch[2], prefix);
        container.appendChild(heading);
        index += 1;
        continue;
      }

      if (/^#{1,6}$/.test(trimmed)) {
        index += 1;
        continue;
      }

      if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
        var rule = document.createElement('hr');
        rule.className = prefix + '__answer-rule';
        container.appendChild(rule);
        index += 1;
        continue;
      }

      if (isTableLine(trimmed)) {
        var tableLines = [];
        while (index < lines.length && isTableLine(lines[index].trim())) {
          tableLines.push(lines[index].trim());
          index += 1;
        }
        appendTable(container, tableLines, prefix);
        continue;
      }

      var listMatch = trimmed.match(/^([-*]|\d+[.)])\s+(.+)$/);
      if (listMatch) {
        var ordered = /^\d/.test(listMatch[1]);
        var list = document.createElement(ordered ? 'ol' : 'ul');
        while (index < lines.length) {
          var itemMatch = lines[index].trim().match(/^([-*]|\d+[.)])\s+(.+)$/);
          if (!itemMatch || /^\d/.test(itemMatch[1]) !== ordered) break;
          var item = document.createElement('li');
          appendInline(item, itemMatch[2], prefix);
          list.appendChild(item);
          index += 1;
        }
        container.appendChild(list);
        continue;
      }

      var paragraph = document.createElement('p');
      var paragraphLines = [];
      while (index < lines.length) {
        var candidate = lines[index].trim();
        if (!candidate || (paragraphLines.length && isBlockStart(candidate))) break;
        paragraphLines.push(lines[index]);
        index += 1;
      }
      paragraphLines.forEach(function (paragraphLine, lineIndex) {
        if (lineIndex) paragraph.appendChild(document.createElement('br'));
        appendInline(paragraph, paragraphLine, prefix);
      });
      container.appendChild(paragraph);
    }

    if (!container.childNodes.length) container.textContent = String(value || '');
  }

  function isBlockStart(value) {
    return /^(?:#{1,6}\s+|[-*]\s+|\d+[.)]\s+|(?:-{3,}|\*{3,}|_{3,})$)/.test(value) || isTableLine(value);
  }

  function isTableLine(value) {
    return value.charAt(0) === '|';
  }

  function tableCells(value) {
    return value.replace(/^\|/, '').replace(/\|$/, '').split('|').map(function (cell) {
      return cell.trim();
    });
  }

  function isTableDivider(value) {
    var cells = tableCells(value);
    return cells.length > 0 && cells.every(function (cell) {
      return /^:?-+:?$/.test(cell);
    });
  }

  function appendTable(container, rows, prefix) {
    if (!rows.length) return;

    var hasHeader = rows.length > 1 && isTableDivider(rows[1]);
    var wrap = document.createElement('div');
    var table = document.createElement('table');
    wrap.className = prefix + '__answer-table-wrap';
    table.className = prefix + '__answer-table';

    if (hasHeader) {
      var head = document.createElement('thead');
      var headRow = document.createElement('tr');
      tableCells(rows[0]).forEach(function (cell) {
        var th = document.createElement('th');
        appendInline(th, cell, prefix);
        headRow.appendChild(th);
      });
      head.appendChild(headRow);
      table.appendChild(head);
    }

    var body = document.createElement('tbody');
    rows.slice(hasHeader ? 2 : 0).forEach(function (row) {
      if (isTableDivider(row)) return;
      var tr = document.createElement('tr');
      tableCells(row).forEach(function (cell) {
        var td = document.createElement('td');
        appendInline(td, cell, prefix);
        tr.appendChild(td);
      });
      body.appendChild(tr);
    });
    if (body.childNodes.length) table.appendChild(body);
    wrap.appendChild(table);
    container.appendChild(wrap);
  }

  function appendInline(container, value, prefix) {
    var text = String(value || '');
    var pattern = /(!\[([^\]]*)\]\((https?:\/\/[^)]+)\)|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`)/gi;
    var cursor = 0;
    var match;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > cursor) container.appendChild(document.createTextNode(text.slice(cursor, match.index)));

      if (match[3]) {
        if (match[2]) container.appendChild(document.createTextNode(match[2]));
      } else if (match[5]) {
        appendLink(container, match[5], match[4], prefix);
      } else if (match[6]) {
        appendElement(container, 'strong', match[6]);
      } else if (match[7]) {
        appendElement(container, 'em', match[7]);
      } else if (match[8]) {
        appendElement(container, 'code', match[8]);
      }

      cursor = pattern.lastIndex;
    }

    var tail = text.slice(cursor);
    if (tail.indexOf('**') === 0) appendElement(container, 'strong', tail.slice(2));
    else if (tail.indexOf('*') === 0) appendElement(container, 'em', tail.slice(1));
    else if (tail.indexOf('`') === 0) appendElement(container, 'code', tail.slice(1));
    else if (tail) container.appendChild(document.createTextNode(tail));
  }

  function appendElement(container, tagName, text) {
    var element = document.createElement(tagName);
    element.textContent = text;
    container.appendChild(element);
  }

  function appendLink(container, href, label, prefix) {
    var link = document.createElement('a');
    link.className = prefix + '__answer-link';
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = label || href;
    container.appendChild(link);
  }

  function appendImage(container, src, alt, prefix) {
    var media = document.createElement('figure');
    var image = document.createElement('img');
    media.className = prefix + '__answer-media';
    image.className = prefix + '__answer-image';
    image.src = src;
    image.alt = alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.addEventListener('error', function () {
      if (media.parentNode) media.parentNode.removeChild(media);
    });
    media.appendChild(image);
    container.appendChild(media);
  }

  return { render: render };
})();

window.CapucinneAIVoice = window.CapucinneAIVoice || (function () {
  // ---- Shared speechSynthesis warm-up -------------------------------------
  // Browsers (especially Chrome and mobile Safari) return an EMPTY voice list
  // from getVoices() until the engine has loaded its voices and fired the
  // 'voiceschanged' event. We prime it once on module load and keep the latest
  // list around so the first real speak() call already has a voice to use.
  var cachedVoices = [];

  function refreshVoiceCache() {
    if (!('speechSynthesis' in window) || typeof window.speechSynthesis.getVoices !== 'function') return;
    var list = window.speechSynthesis.getVoices();
    if (list && list.length) cachedVoices = list;
  }

  if ('speechSynthesis' in window) {
    refreshVoiceCache();
    if (typeof window.speechSynthesis.addEventListener === 'function') {
      window.speechSynthesis.addEventListener('voiceschanged', refreshVoiceCache);
    } else if ('onvoiceschanged' in window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = refreshVoiceCache;
    }
  }

  // Wait (briefly) for voices to be available, then run the callback. Resolves
  // immediately when voices are already cached so a user-gesture speak() is not
  // deferred out of the gesture call stack on mobile.
  function whenVoicesReady(callback) {
    refreshVoiceCache();
    if (cachedVoices.length || !('speechSynthesis' in window)) {
      callback(cachedVoices);
      return;
    }

    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      refreshVoiceCache();
      callback(cachedVoices);
    };

    if (typeof window.speechSynthesis.addEventListener === 'function') {
      window.speechSynthesis.addEventListener('voiceschanged', finish, { once: true });
    }
    // Safety net: some browsers never fire voiceschanged but DO speak fine with
    // the default voice, so proceed regardless after a short wait.
    window.setTimeout(finish, 250);
  }

  // Mobile browsers only allow speech that originates from a user gesture.
  // Speaking a single silent utterance synchronously inside a click/tap handler
  // "unlocks" the engine for this page so subsequent (async) speak() calls —
  // e.g. reading an answer that arrives from a fetch — are allowed to play.
  var speechUnlocked = false;
  // ONE shared <audio> element, first played (silently) inside the shopper's
  // tap: iOS Safari only lets an element play that was started in a gesture,
  // so the worker's MP3 (fetched async later) is played through THIS element
  // instead of a fresh `new Audio()` - which iOS rejected, silently sending
  // every answer to the browser voice (merchant iPhone test 2026-09-03).
  var sharedAudio = null;
  var SILENT_MP3 = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7////////////////////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQCgAAAAAAAAAGGX0zXQAAAAAAAAAAAAAAAAAAAAP/7EGQAAAAAAaQAAAAAAAADSAAAAAAAAAB0AAAAAAAAADoAAAAATEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

  function unlockAudioElement() {
    if (sharedAudio || !window.Audio) return;
    try {
      var element = new window.Audio();
      element.preload = 'auto';
      element.setAttribute('playsinline', '');
      element.src = SILENT_MP3;
      var result = element.play();
      if (result && typeof result.catch === 'function') result.catch(function () { /* muted tab / autoplay policy: the real play() will try again */ });
      sharedAudio = element;
    } catch (error) {
      sharedAudio = null;
    }
  }

  function unlockSpeech() {
    unlockAudioElement();
    if (speechUnlocked || !('speechSynthesis' in window) || !window.SpeechSynthesisUtterance) return;
    try {
      var primer = new window.SpeechSynthesisUtterance('');
      primer.volume = 0;
      window.speechSynthesis.speak(primer);
      window.speechSynthesis.cancel();
      speechUnlocked = true;
    } catch (error) {
      // If the primer throws, the real speak() will still try inside its gesture.
    }
    refreshVoiceCache();
  }

  // WebKit hands recognition.lang to the system recognizer, which wants a
  // region-qualified tag: a bare 'en' (Shopify's request.locale.iso_code) can
  // come back as language-not-supported on iPhone. Prefer the device's own
  // locale when it shares the primary subtag, else a sensible default.
  function resolveRecognitionLang(locale) {
    var lang = String(locale || '').trim();
    if (!lang) lang = String(document.documentElement.lang || '').trim() || 'en-US';
    if (lang.indexOf('-') !== -1) return lang;
    var primary = lang.toLowerCase();
    var device = String((navigator && navigator.language) || '');
    if (device.toLowerCase().indexOf(primary + '-') === 0) return device;
    var defaults = { en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', it: 'it-IT', sl: 'sl-SI', hr: 'hr-HR', sr: 'sr-RS', nl: 'nl-NL', pt: 'pt-PT' };
    return defaults[primary] || lang;
  }

  function pickVoice(voices, locale) {
    var language = String(locale || 'en-US').toLowerCase().split('-')[0];
    var list = voices && voices.length ? voices : cachedVoices;
    return list.filter(function (voice) {
      return voice && String(voice.lang || '').toLowerCase().indexOf(language) === 0;
    })[0] || list[0] || null;
  }

  function create(options) {
    options = options || {};

    var buttons = Array.prototype.slice.call(options.buttons || []);
    var outputButtons = Array.prototype.slice.call(options.outputButtons || []);
    var endButtons = Array.prototype.slice.call(options.endButtons || []);
    var statusTargets = Array.prototype.slice.call(options.statusTargets || []);

    // The .cap-ai-consultant__voice-controls wrappers that hold each mic/speaker/
    // End trio. Deriving them from the mic buttons lets us reflect the active
    // conversation state onto the whole cluster (and its composer) for the
    // apartments.com-style "this is a live voice conversation" treatment.
    var groups = [];
    buttons.forEach(function (button) {
      var group = button && button.closest ? button.closest('.cap-ai-consultant__voice-controls') : null;
      if (group && groups.indexOf(group) === -1) groups.push(group);
      var composer = button && button.closest ? button.closest('.cap-ai-consultant__composer') : null;
      if (composer && groups.indexOf(composer) === -1) groups.push(composer);
    });
    var enabled = false;
    var inputMuted = false;
    var outputEnabled = readOutputPreference();
    var state = 'off';
    var activeInput = null;
    var activeButton = null;
    var recognition = null;
    var presentationTimer = null;
    var presentationToken = 0;
    var activePresentationFinish = null;
    var activeAudio = null;
    var activeAudioUrl = '';
    var activeVoiceAbort = null;
    // The last assistant answer + the element it was rendered into, so the
    // standalone speaker button can re-speak it on demand even in text mode.
    var lastSpokenText = '';
    var lastSpokenContainer = null;
    // Why the last listen ended without a transcript ('' = it did not fail):
    // 'no-speech', 'aborted', 'audio-capture', 'network', 'start-failed'.
    // Drives the paused label so the shopper is told to tap again, and lets
    // the next tap LISTEN AGAIN instead of muting.
    var lastListenError = '';
    var BLOCKED_MIC_HINT = "Microphone blocked - allow it in your browser settings, or use the keyboard's microphone key to dictate";
    var UNSUPPORTED_HINT = "Voice isn't available in this browser - use the keyboard's microphone key to dictate";
    // A short notice ABOVE the composer (data-voice-notice pill) for the cases
    // the status pill cannot show because no voice session is live: blocked
    // microphone, unsupported browser. The native placeholder is transparent
    // on phones, so a hint written there was never seen (iPhone test).
    var noticeTimer = null;
    function showNotice(text, ms) {
      clearNotice();
      statusTargets.forEach(function (target) {
        if (target) target.setAttribute('data-voice-notice', text);
      });
      noticeTimer = window.setTimeout(clearNotice, ms || 5200);
    }
    function clearNotice() {
      if (noticeTimer) window.clearTimeout(noticeTimer);
      noticeTimer = null;
      statusTargets.forEach(function (target) {
        if (target) target.removeAttribute('data-voice-notice');
      });
    }

    function setState(nextState) {
      state = nextState;
      var label = stateLabel(nextState);

      buttons.forEach(function (button) {
        button.classList.toggle('is-listening', nextState === 'listening');
        button.classList.toggle('is-speaking', nextState === 'speaking');
        button.classList.toggle('is-thinking', nextState === 'thinking');
        button.classList.toggle('is-voice-active', enabled);
        button.classList.toggle('is-input-muted', enabled && inputMuted);
        button.setAttribute('aria-pressed', enabled && !inputMuted ? 'true' : 'false');

        var inputLabel = !enabled
          ? 'Start voice conversation'
          : (inputMuted ? 'Turn microphone on' : 'Turn microphone off');
        button.setAttribute('aria-label', inputLabel);
        button.setAttribute('title', inputLabel);
      });

      outputButtons.forEach(function (button) {
        var outputLabel = outputEnabled ? 'Turn spoken responses off' : 'Turn spoken responses on';
        button.hidden = false;
        // The speaker is "active" (currently reading aloud) only while spoken
        // responses are on AND the assistant is speaking — used to animate it.
        button.classList.toggle('is-speaking', outputEnabled && nextState === 'speaking');
        button.classList.toggle('is-voice-active', enabled);
        button.classList.toggle('is-output-muted', !outputEnabled);
        button.setAttribute('aria-pressed', outputEnabled ? 'true' : 'false');
        button.setAttribute('aria-label', outputLabel);
        button.setAttribute('title', outputLabel);
      });

      endButtons.forEach(function (button) {
        button.hidden = !enabled;
        button.classList.toggle('is-voice-active', enabled);
      });

      // Reflect the whole conversation state onto the controls group + composer
      // so the active 3-control model can be styled as one unit (the listening /
      // thinking / speaking cue applies to the cluster, not just the mic).
      groups.forEach(function (group) {
        if (!group) return;
        group.classList.toggle('is-voice-active', enabled);
        group.classList.toggle('is-listening', enabled && nextState === 'listening');
        group.classList.toggle('is-thinking', enabled && nextState === 'thinking');
        group.classList.toggle('is-speaking', enabled && nextState === 'speaking');
        group.classList.toggle('is-output-muted', enabled && !outputEnabled);
      });

      statusTargets.forEach(function (target) {
        if (!target) return;
        if (enabled && label) {
          target.setAttribute('data-voice-status', label);
          target.setAttribute('data-voice-output', outputEnabled ? 'on' : 'off');
        } else {
          target.removeAttribute('data-voice-status');
          target.removeAttribute('data-voice-output');
        }
      });

      if (typeof options.onStateChange === 'function') options.onStateChange(nextState, enabled);
    }

    function stateLabel(value) {
      if (value === 'listening') return 'Listening';
      if (value === 'thinking') return 'Thinking';
      if (value === 'speaking') return 'Speaking';
      if (value === 'paused') {
        if (inputMuted) return 'Microphone off';
        if (lastListenError) return "Didn't catch that - tap the microphone to try again";
        return 'Tap the microphone to continue';
      }
      return '';
    }

    function toggle(input, button) {
      // Runs inside the mic button's click handler. Recognition is started
      // FIRST and the TTS unlock (silent primer + audio element) runs right
      // after, still inside the same gesture - the primer must never race the
      // recognizer for the audio session on iPhone.
      clearNotice();

      if (enabled && inputMuted) {
        inputMuted = false;
        listen(input || activeInput, button || activeButton);
        unlockSpeech();
        return;
      }

      // Listening ended without a transcript (iOS ends a session after a short
      // silence, or raised no-speech / audio-capture / network): the tap means
      // "try again", not "mute" - muting here made the mic feel dead on iPhone.
      if (enabled && !recognition && state === 'paused') {
        listen(input || activeInput, button || activeButton);
        unlockSpeech();
        return;
      }

      if (enabled) {
        muteInput();
        return;
      }

      enabled = true;
      inputMuted = false;
      setOutputEnabled(true);
      listen(input, button);
      unlockSpeech();
    }

    function muteInput() {
      inputMuted = true;

      if (recognition) {
        recognition.onend = null;
        recognition.stop();
      }

      recognition = null;
      setState(state === 'listening' ? 'paused' : state);
    }

    function activatePending(input, button) {
      enabled = true;
      inputMuted = false;
      setOutputEnabled(true);
      activeInput = input || activeInput;
      activeButton = button || activeButton || buttons[0] || null;

      setState('thinking');
    }

    function listen(input, button) {
      var Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      activeInput = input || activeInput;
      activeButton = button || activeButton || buttons[0] || null;

      if (inputMuted) {
        setState('paused');
        return;
      }

      if (!Recognition) {
        // In-app browsers (Instagram, WhatsApp, Gmail...) and non-Safari iOS
        // browsers have no SpeechRecognition: say so where the shopper looks
        // and hand them the keyboard's own dictation key (the input keeps
        // focus for exactly that).
        if (activeInput) {
          try { activeInput.focus({ preventScroll: true }); } catch (error) { try { activeInput.focus(); } catch (e) { /* ignore */ } }
        }
        if (activeButton) {
          activeButton.classList.add('is-unsupported');
          window.setTimeout(function () { activeButton.classList.remove('is-unsupported'); }, 2600);
        }
        enabled = false;
        setState('off');
        showNotice(UNSUPPORTED_HINT);
        return;
      }

      cancelPresentation();
      if (recognition) recognition.stop();

      recognition = new Recognition();
      var submitted = false;
      var failed = false;

      recognition.lang = resolveRecognitionLang(options.locale || document.documentElement.lang || 'en-US');
      recognition.interimResults = true;
      recognition.continuous = false;
      setState('listening');

      var lastInterim = '';
      recognition.onresult = function (event) {
        var transcript = '';
        var isFinal = false;

        for (var index = event.resultIndex; index < event.results.length; index += 1) {
          transcript += event.results[index][0].transcript;
          if (event.results[index].isFinal) isFinal = true;
        }

        transcript = transcript.trim();
        if (activeInput && transcript) activeInput.value = transcript;
        if (transcript) lastInterim = transcript;

        if (isFinal && transcript && !submitted) {
          submitted = true;
          lastListenError = '';
          setState('thinking');
          if (typeof options.onTranscript === 'function') options.onTranscript(transcript, activeInput, activeButton);
        }
      };

      lastListenError = '';
      recognition.onerror = function (event) {
        failed = true;
        var code = event && event.error ? String(event.error) : 'unknown';

        if (code === 'not-allowed' || code === 'service-not-allowed') {
          // Permission declined, Siri & Dictation off, or an in-app browser:
          // say so where the shopper is looking, and hand them the keyboard's
          // own dictation key. (A 1.4 s red flash was the only signal before.)
          enabled = false;
          lastListenError = code;
          if (activeInput) {
            try { activeInput.focus({ preventScroll: true }); } catch (error) { try { activeInput.focus(); } catch (e) { /* ignore */ } }
          }
          showNotice(BLOCKED_MIC_HINT, 6500);
          if (activeButton) {
            activeButton.classList.add('is-unsupported');
            window.setTimeout(function () {
              activeButton.classList.remove('is-unsupported');
            }, 2600);
          }
          setState('off');
          return;
        }

        // no-speech / aborted / audio-capture / network: recoverable - onend
        // parks the mic with a "tap to try again" label and the next tap
        // listens again (see toggle).
        lastListenError = code;
      };

      recognition.onend = function () {
        recognition = null;
        // iOS Safari can hand over the words only as interim results and then
        // end the session without a final one: send what was heard rather
        // than parking with the text stranded in the input.
        if (enabled && !submitted && !failed && lastInterim) {
          submitted = true;
          lastListenError = '';
          setState('thinking');
          if (typeof options.onTranscript === 'function') options.onTranscript(lastInterim, activeInput, activeButton);
          return;
        }
        if (enabled && !submitted) setState('paused');
      };

      try {
        recognition.start();
      } catch (error) {
        // iOS throws when a session is already running or the call landed
        // outside a gesture: park the mic recoverably instead of dying silent.
        recognition = null;
        lastListenError = 'start-failed';
        if (enabled) setState('paused');
      }
    }

    function present(container, text, onComplete) {
      if (!container) return;

      cancelPresentation();
      var token = ++presentationToken;
      var value = String(text || '');
      var spoken = toSpeechText(value);
      var finished = false;

      // Remember this answer so the speaker button can replay it on demand,
      // even after the typing/reveal animation has completed (text mode too).
      lastSpokenText = spoken;
      lastSpokenContainer = container;

      function renderValue(partialValue) {
        var renderer = options.renderProgress || options.renderFinal;
        if (typeof renderer === 'function') renderer(container, partialValue);
        else container.textContent = partialValue;
      }

      // The reveal is driven by TWO sources while speaking: the speech engine's
      // word-boundary events (accurate, but unsupported on some browsers) and a
      // steady fallback interval. Track a high-water mark so the two never fight
      // and the text only ever moves forward in sync with the spoken answer.
      var revealedRatio = 0;
      function reveal(ratio) {
        if (token !== presentationToken) return;
        if (ratio <= revealedRatio) return;
        revealedRatio = ratio;
        var end = Math.max(1, Math.min(value.length, Math.ceil(value.length * ratio)));
        renderValue(value.slice(0, end));
      }

      function finish() {
        if (finished || token !== presentationToken) return;
        finished = true;
        activePresentationFinish = null;
        if (presentationTimer) window.clearInterval(presentationTimer);
        presentationTimer = null;
        cleanupAudio();
        renderValue(value);
        if (typeof onComplete === 'function') onComplete();

        if (enabled) {
          setState('paused');
          window.setTimeout(function () {
            if (enabled && !inputMuted && state === 'paused') listen(activeInput, activeButton);
          }, 320);
        }
      }

      activePresentationFinish = finish;

      if (!value || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches && !enabled)) {
        finish();
        return;
      }

      renderValue('');

      // Read every answer aloud whenever spoken responses are ON — it does NOT
      // require voice/mic mode. So a shopper who simply types a question still
      // hears the reply spoken as it streams, as long as the sound toggle is on.
      if (outputEnabled && spoken) {
        setState('speaking');
        var voiceRatio = 0;
        presentationTimer = window.setInterval(function () {
          voiceRatio = Math.min(0.98, voiceRatio + 0.018);
          reveal(voiceRatio);
        }, 110);
        speakResponse(spoken, token, finish, reveal);
        return;
      }

      var wordEnds = [];
      var wordMatch;
      var wordPattern = /\S+\s*/g;
      while ((wordMatch = wordPattern.exec(value)) !== null) {
        wordEnds.push(wordPattern.lastIndex);
      }
      var visibleWords = 0;

      presentationTimer = window.setInterval(function () {
        visibleWords = Math.min(wordEnds.length, visibleWords + 2);
        renderValue(value.slice(0, wordEnds[visibleWords - 1] || value.length));
        if (visibleWords >= wordEnds.length) finish();
      }, 90);
    }

    // AUDIO-ONLY playback of an answer, on its OWN track. The consultant page
    // renders the visible text itself (immediately, never gated by voice), so
    // this never touches the bubble's text or owns the turn's completion — it
    // only plays the spoken audio (ElevenLabs, browser TTS fallback) alongside.
    //
    // Returns true when it started a spoken track, false when there was nothing
    // to do (no text, or spoken responses are off) so the caller can ignore it.
    // An optional onHighlight(ratio) callback receives the spoken progress
    // (0..1) so the caller may add a subtle word highlight WITHOUT the audio
    // controlling when the text appears. Fully fail-safe: any failure simply
    // means no audio, and the already-rendered text stands on its own.
    function speak(text, onHighlight, container) {
      var value = String(text || '');
      var spoken = toSpeechText(value);

      // Remember the answer + its container so the standalone speaker button can
      // replay it on demand later, exactly as present() used to record it.
      lastSpokenText = spoken;
      if (container) lastSpokenContainer = container;

      if (!outputEnabled || !spoken) return false;

      // Start a fresh presentation token so a prior in-flight audio track is
      // cancelled and this one owns playback. This does NOT touch the text.
      cancelPresentation();
      var token = ++presentationToken;

      setState('speaking');

      var finished = false;
      var finish = function () {
        if (finished || token !== presentationToken) return;
        finished = true;
        activePresentationFinish = null;
        cleanupAudio();
        // Return the mic to a listening posture if this is a live voice
        // conversation — mirrors present()'s post-speech behavior. In pure
        // text-mode (sound on, no mic) just clear the speaking indicator.
        if (enabled) {
          setState('paused');
          window.setTimeout(function () {
            if (enabled && !inputMuted && state === 'paused') listen(activeInput, activeButton);
          }, 320);
        } else if (state === 'speaking') {
          setState('off');
        }
      };
      activePresentationFinish = finish;

      var reveal = function (ratio) {
        if (token !== presentationToken) return;
        if (typeof onHighlight === 'function') onHighlight(Math.max(0, Math.min(1, ratio)));
      };

      speakResponse(spoken, token, finish, reveal);
      return true;
    }

    // Re-speak the most recent assistant answer on demand. Invoked from the
    // standalone speaker button so the shopper can replay the last reply even
    // in text mode (no active mic). MUST be called from the button's click
    // handler so the first speak() runs inside a user-gesture call stack —
    // mobile browsers silently drop speech that starts outside a gesture.
    function speakLast() {
      // Runs inside the speaker button's click handler — unlock first so the
      // utterance plays on mobile.
      unlockSpeech();
      if (!lastSpokenText) return false;

      // A manual replay implies the shopper wants to hear responses.
      if (!outputEnabled) setOutputEnabled(true);

      cancelPresentation();
      var token = ++presentationToken;
      var container = lastSpokenContainer;
      var spoken = lastSpokenText;

      setState('speaking');

      var finish = function () {
        cleanupAudio();
        if (state === 'speaking') setState(enabled ? 'paused' : 'off');
      };
      activePresentationFinish = finish;

      speakResponse(spoken, token, finish, function (ratio) {
        // No re-typing on replay — the text is already fully visible.
        if (container && typeof options.renderFinal === 'function' && ratio >= 0.999) {
          options.renderFinal(container, spoken);
        }
      });

      return true;
    }

    function speakResponse(spoken, token, finish, reveal) {
      var voiceEndpoint = String(options.voiceEndpoint || '').trim();

      if (!voiceEndpoint || !window.fetch || !window.Audio || !window.URL || !window.URL.createObjectURL) {
        speakWithBrowser(spoken, token, finish, reveal);
        return;
      }

      // Abortable so muting (spoken responses off) stops the ElevenLabs request
      // mid-generation instead of letting it finish and play after the mute.
      var controller = (typeof window.AbortController === 'function') ? new window.AbortController() : null;
      activeVoiceAbort = controller;

      var fetchOptions = {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'audio/mpeg,audio/*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: spoken })
      };
      if (controller) fetchOptions.signal = controller.signal;

      window.fetch(voiceEndpoint, fetchOptions)
        .then(function (response) {
          if (!response.ok) throw new Error('Voice endpoint returned ' + response.status);
          return response.blob();
        })
        .then(function (blob) {
          // Stale answer, or the shopper muted while this was generating — never
          // start playback after a mute.
          if (token !== presentationToken || !outputEnabled) return;
          if (!blob || !blob.size || !/^audio\//i.test(blob.type || 'audio/mpeg')) {
            throw new Error('Voice endpoint returned no audio');
          }

          activeAudioUrl = window.URL.createObjectURL(blob);
          // The element unlocked in the tap (see unlockAudioElement) is the
          // one iOS lets us play now; a fresh Audio() here is rejected there.
          activeAudio = sharedAudio || new window.Audio();
          activeAudio.src = activeAudioUrl;
          var fallbackStarted = false;
          var fallbackToBrowser = function () {
            if (fallbackStarted || token !== presentationToken || !outputEnabled) return;
            fallbackStarted = true;
            cleanupAudio();
            speakWithBrowser(spoken, token, finish, reveal);
          };
          activeAudio.preload = 'auto';
          activeAudio.onended = finish;
          activeAudio.onerror = fallbackToBrowser;

          var playResult = activeAudio.play();
          if (playResult && typeof playResult.catch === 'function') {
            playResult.catch(fallbackToBrowser);
          }
        })
        .catch(function () {
          // An aborted fetch lands here too — do not fall back to browser speech
          // if the answer is stale or the shopper muted.
          if (token !== presentationToken || !outputEnabled) return;
          cleanupAudio();
          speakWithBrowser(spoken, token, finish, reveal);
        });
    }

    function speakWithBrowser(spoken, token, finish, reveal) {
      if (!('speechSynthesis' in window) || !window.SpeechSynthesisUtterance) {
        finish();
        return;
      }

      var synthesis = window.speechSynthesis;
      var locale = options.locale || document.documentElement.lang || 'en-US';

      // Clear any stale/paused utterance BEFORE we build the new one. Calling
      // cancel() once up front (rather than racing it against speak()) avoids
      // the common bug where a late cancel() kills the utterance we just queued.
      synthesis.cancel();

      whenVoicesReady(function (voices) {
        if (token !== presentationToken) return;

        var utterance = new window.SpeechSynthesisUtterance(spoken);
        utterance.lang = locale;
        utterance.rate = 0.96;
        utterance.pitch = 1;
        utterance.volume = 1;
        utterance.voice = pickVoice(voices, locale);

        utterance.onboundary = function (event) {
          if (token !== presentationToken) return;
          if (typeof reveal !== 'function') return;
          var ratio = spoken.length ? event.charIndex / spoken.length : 1;
          reveal(Math.min(0.98, ratio));
        };
        utterance.onend = finish;
        utterance.onerror = finish;

        // Chrome can leave the engine in a 'paused' state after a prior
        // cancel(); resume() before speak() makes the new utterance audible.
        try { synthesis.resume(); } catch (error) { /* not all engines support resume */ }
        synthesis.speak(utterance);

        // Chrome desktop bug: long utterances stop after ~15s unless we nudge
        // the engine with pause()/resume() while it is speaking. Harmless on
        // browsers that don't need it.
        var keepAlive = window.setInterval(function () {
          if (token !== presentationToken || !synthesis.speaking) {
            window.clearInterval(keepAlive);
            return;
          }
          try {
            synthesis.pause();
            synthesis.resume();
          } catch (error) {
            window.clearInterval(keepAlive);
          }
        }, 10000);
      });
    }

    function cleanupAudio() {
      // Abort any in-flight ElevenLabs request so it stops generating audio.
      if (activeVoiceAbort) {
        try { activeVoiceAbort.abort(); } catch (error) { /* already settled */ }
        activeVoiceAbort = null;
      }
      if (activeAudio) {
        activeAudio.onended = null;
        activeAudio.onerror = null;
        activeAudio.pause();
        activeAudio.removeAttribute('src');
        activeAudio.load();
      }
      activeAudio = null;
      if (activeAudioUrl && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(activeAudioUrl);
      }
      activeAudioUrl = '';
    }

    function cancelPresentation() {
      presentationToken += 1;
      activePresentationFinish = null;
      if (presentationTimer) window.clearInterval(presentationTimer);
      presentationTimer = null;
      cleanupAudio();
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    }

    function toggleOutput() {
      setOutputEnabled(!outputEnabled);
    }

    function setOutputEnabled(nextEnabled) {
      var next = Boolean(nextEnabled);
      if (next === outputEnabled) return;

      outputEnabled = next;
      writeOutputPreference(outputEnabled);

      if (!outputEnabled && state === 'speaking') {
        // Muting must stop ALL sound and generation immediately: cancel browser
        // speech, abort the ElevenLabs request + playback (cleanupAudio), then
        // finish the answer's text silently and clear the speaking indicator.
        var finish = activePresentationFinish;
        if ('speechSynthesis' in window) {
          try { window.speechSynthesis.cancel(); } catch (error) { /* engine busy */ }
        }
        cleanupAudio();
        if (finish) finish();
        setState(enabled ? 'paused' : 'off');
        return;
      }

      setState(state);
    }

    function stop() {
      enabled = false;
      inputMuted = false;
      if (recognition) {
        recognition.onend = null;
        recognition.stop();
      }
      recognition = null;

      var finish = activePresentationFinish;
      if (finish) finish();
      cancelPresentation();
      setState('off');
    }

    function readOutputPreference() {
      try {
        // Spoken responses are OPT-IN (default off): answers aren't auto-read for
        // every shopper, and product cards/quick replies aren't gated behind the
        // spoken audio finishing. Tapping the speaker (a gesture) turns sound on
        // for the session, after which every answer is read aloud as it streams.
        return window.localStorage.getItem('capucinne-ai-spoken-responses') === 'on';
      } catch (error) {
        return false;
      }
    }

    function writeOutputPreference(value) {
      try {
        window.localStorage.setItem('capucinne-ai-spoken-responses', value ? 'on' : 'off');
      } catch (error) {
        // Voice output still works for this session when storage is unavailable.
      }
    }

    function toSpeechText(value) {
      return String(value || '')
        .replace(/!\[[^\]]*]\([^)]+\)/g, ' ')
        .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/[`*_#>~|-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 1400);
    }

    setState('off');

    return {
      activatePending: activatePending,
      hasSpokenAnswer: function () { return Boolean(lastSpokenText); },
      isEnabled: function () { return enabled; },
      isOutputEnabled: function () { return outputEnabled; },
      // Unlock the speech engine from within a user gesture (e.g. the send tap)
      // so an auto-spoken answer can play on mobile, which blocks speech that
      // doesn't originate in a gesture call stack.
      prime: function () { unlockSpeech(); },
      present: present,
      // Audio-only playback on its own track (text is rendered by the caller,
      // never gated behind voice). See speak() above.
      speak: speak,
      setOutputEnabled: setOutputEnabled,
      speakLast: speakLast,
      stop: stop,
      toggle: toggle,
      toggleOutput: toggleOutput
    };
  }

  return { create: create };
})();

/*
 * AI sales attribution (2026-10-13). Ties a shopper who has chatted with the AI
 * consultant to what they do next, for the dashboard's AI sales funnel:
 *   - the cart carries the conversation id in the hidden cart attribute
 *     _capucinne_ai_conversation, which Shopify copies onto the order
 *     (purchases);
 *   - every successful /cart/add, anywhere on the site, sends a small beacon to
 *     the Worker's /event route (add to cart).
 * Shoppers who never sent the AI a message are left untouched. Fail-safe: a
 * tracking problem can never block or change an add to cart.
 */
window.CapucinneAIAttribution = window.CapucinneAIAttribution || (function () {
  var CART_ATTRIBUTE = '_capucinne_ai_conversation';
  var TAGGED_KEY = 'capucinne_ai_cart_tagged';
  var EVENT_ENDPOINT = '/apps/capucinne-consultant/event';
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch : null;

  // Respect a visitor who declined analytics in the cookie banner (Shopify's
  // customer privacy API; absent = no banner decision applies).
  function analyticsAllowed() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return true;
    try { return !!privacy.analyticsProcessingAllowed(); } catch (error) { return true; }
  }

  // The stored conversation's id once the shopper has sent the AI a message.
  function conversationId() {
    var store = window.CapucinneAIConversation;
    if (!store) return '';
    try {
      var session = store.load();
      var chatted = (session.history || []).some(function (turn) { return turn && turn.role === 'user'; });
      return chatted && session.conversationId ? String(session.conversationId) : '';
    } catch (error) {
      return '';
    }
  }

  function cartToken() {
    var match = String(document.cookie || '').match(/(?:^|;\s*)cart=([^;]+)/);
    if (!match) return '';
    try { return decodeURIComponent(match[1]).split('?')[0]; } catch (error) { return match[1]; }
  }

  function routeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root.charAt(0) === '/' ? root : '/';
  }

  // Write the conversation id onto the current cart, once per cart.
  function tagCart() {
    var id = conversationId();
    var token = cartToken();
    if (!id || !token || !nativeFetch || !analyticsAllowed()) return;
    var marker = id + '|' + token;
    try { if (window.localStorage.getItem(TAGGED_KEY) === marker) return; } catch (error) { /* tag anyway */ }
    var attributes = {};
    attributes[CART_ATTRIBUTE] = id;
    nativeFetch.call(window, routeRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      // Survives a straight jump to checkout right after the add.
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response || !response.ok) return;
      try { window.localStorage.setItem(TAGGED_KEY, marker); } catch (error) { /* retried next page */ }
    }).catch(function () { /* retried on the next page */ });
  }

  function itemsFrom(json) {
    var list = json && Array.isArray(json.items) ? json.items : (json && json.variant_id ? [json] : []);
    return list.filter(function (item) { return item && typeof item === 'object'; }).slice(0, 20).map(function (item) {
      var price = item.final_line_price != null ? item.final_line_price : item.line_price;
      return {
        product_title: String(item.product_title || item.title || '').slice(0, 200),
        variant_id: String(item.variant_id || item.id || ''),
        quantity: Number(item.quantity) || 0,
        line_price: price != null && Number.isFinite(Number(price)) ? Number(price) : null
      };
    });
  }

  function report(type, items) {
    var id = conversationId();
    if (!id || !nativeFetch || !analyticsAllowed()) return;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    try {
      nativeFetch.call(window, EVENT_ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ type: type, conversation_id: id, currency: currency, items: items || [] })
      }).catch(function () { /* a lost beacon only costs one funnel count */ });
    } catch (error) { /* ignore */ }
  }

  function isCartAdd(resource, init) {
    var url = typeof resource === 'string' ? resource
      : (resource && typeof resource.url === 'string') ? resource.url
        : (resource && typeof resource.href === 'string') ? resource.href : '';
    var method = (init && init.method) || (resource && typeof resource === 'object' && resource.method) || 'GET';
    return String(method).toUpperCase() === 'POST' && /(^|\/)cart\/add(\.js)?$/.test(String(url).split(/[?#]/)[0]);
  }

  // Every add to cart in the theme, the consultant chat and the landing pages
  // goes through fetch('/cart/add.js'); watch those without touching them.
  if (nativeFetch && !nativeFetch.capucinneAiWrapped) {
    var watchedFetch = function (resource, init) {
      var pending = nativeFetch.apply(window, arguments);
      try {
        if (isCartAdd(resource, init) && conversationId()) {
          pending.then(function (response) {
            if (!response || !response.ok) return;
            response.clone().json().then(function (json) {
              report('add_to_cart', itemsFrom(json));
            }, function () {
              report('add_to_cart', []);
            }).then(tagCart);
          }, function () { /* failed add: nothing to report */ });
        }
      } catch (error) { /* never disturb the request */ }
      return pending;
    };
    watchedFetch.capucinneAiWrapped = true;
    window.fetch = watchedFetch;
  }

  // Tag an existing cart on every page, and right after a message on the
  // consultant page (the conversation is stored by then).
  window.setTimeout(tagCart, 1500);
  document.addEventListener('capucinne:ai-event', function (event) {
    if (event && event.detail && event.detail.event === 'capucinne_ai_message_sent') window.setTimeout(tagCart, 1500);
  });

  return { conversationId: conversationId, tagCart: tagCart };
})();

/*
 * Lead tracking (2026-10-13): a booked Calendly call is a lead in Meta and
 * Google Ads. Calendly's popup/inline widget posts calendly.event_scheduled to
 * the page when the invitee confirms; on that this sends
 *   - Meta Pixel "Lead" to the pixel in Theme settings only (trackSingle), with
 *     the booking id as eventID - no PageView, the Facebook app sends those;
 *   - a Google Ads conversion, once Theme settings hold its send_to
 *     ("AW-.../label");
 *   - generate_lead to the GTM dataLayer, and capucinne_lead to Shopify
 *     customer events (for custom pixels).
 * Meta and Google Ads only fire when the visitor allows marketing cookies. A
 * booking made in a separate Calendly tab is not visible to this page.
 */
window.CapucinneLeads = window.CapucinneLeads || (function () {
  var SENT_KEY = 'capucinne_leads_sent';
  var metaReady = {};
  var adsReady = false;

  function config() {
    return window.CapucinneLeadConfig || {};
  }

  function alreadySent(id) {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(SENT_KEY) || '[]');
      if (list.indexOf(id) !== -1) return true;
      list.push(id);
      window.sessionStorage.setItem(SENT_KEY, JSON.stringify(list.slice(-20)));
    } catch (error) { /* storage off: send */ }
    return false;
  }

  function withMarketingConsent(callback) {
    var shopify = window.Shopify;
    // Marketing consent AND no "do not sell/share" opt-out (US state laws:
    // marketingAllowed() alone is true there by default).
    function allowed() {
      var privacy = shopify && shopify.customerPrivacy;
      if (!privacy || typeof privacy.marketingAllowed !== 'function' || !privacy.marketingAllowed()) return false;
      return typeof privacy.saleOfDataAllowed !== 'function' || !!privacy.saleOfDataAllowed();
    }
    if (shopify && shopify.customerPrivacy) { callback(allowed()); return; }
    if (shopify && typeof shopify.loadFeatures === 'function') {
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          callback(!error && allowed());
        });
        return;
      } catch (error) { /* no consent API: treat as not allowed */ }
    }
    callback(false);
  }

  function sendMeta(eventId, params) {
    var pixelId = String(config().metaPixelId || '').trim();
    if (!/^\d{6,20}$/.test(pixelId)) return;
    if (typeof window.fbq !== 'function') {
      // Meta's standard base code, without its PageView call.
      (function (f, b, e, v) {
        var n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
        var t = b.createElement(e); t.async = true; t.src = v;
        var s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    if (!metaReady[pixelId]) {
      // Only the Lead: no automatic button-click/microdata events and no
      // PageViews on the theme's variant URL changes.
      window.fbq.disablePushState = true;
      window.fbq('set', 'autoConfig', false, pixelId);
      window.fbq('init', pixelId);
      metaReady[pixelId] = true;
    }
    window.fbq('trackSingle', pixelId, 'Lead', params, { eventID: eventId });
  }

  function sendGoogleAds(eventId) {
    var sendTo = String(config().googleAdsSendTo || '').trim();
    if (!/^AW-\d+\/[\w-]+$/.test(sendTo)) return;
    var adsId = sendTo.split('/')[0];
    window.dataLayer = window.dataLayer || [];
    // A local gtag: setting window.gtag would route every AI tracking event to
    // Google Ads too (CapucinneAITrack calls window.gtag when it exists).
    var gtag = function () { window.dataLayer.push(arguments); };
    if (!adsReady) {
      if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
        var script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(adsId);
        document.head.appendChild(script);
        gtag('js', new Date());
      }
      gtag('config', adsId, { send_page_view: false });
      adsReady = true;
    }
    gtag('event', 'conversion', { send_to: sendTo, transaction_id: eventId });
  }

  function track(source, bookingId) {
    var eventId = 'lead-' + source + '-' + (bookingId || Date.now().toString(36));
    if (alreadySent(eventId)) return;
    var aiConversation = window.CapucinneAIAttribution ? window.CapucinneAIAttribution.conversationId() : '';
    try {
      (window.dataLayer = window.dataLayer || []).push({ event: 'generate_lead', lead_source: source, lead_id: eventId, ai_engaged: !!aiConversation });
    } catch (error) { /* no GTM */ }
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        analytics.publish('capucinne_lead', { source: source, lead_id: eventId, ai_conversation_id: aiConversation });
      }
    } catch (error) { /* no customer events */ }
    withMarketingConsent(function (allowed) {
      if (!allowed) return;
      try { sendMeta(eventId, { content_name: source === 'calendly' ? 'Calendly consultation' : source, lead_source: source }); } catch (error) { /* ignore */ }
      try { sendGoogleAds(eventId); } catch (error) { /* ignore */ }
    });
  }

  window.addEventListener('message', function (event) {
    if (!event || !/^https:\/\/([a-z0-9-]+\.)*calendly\.com$/i.test(String(event.origin || ''))) return;
    var data = event.data;
    if (!data || typeof data !== 'object' || data.event !== 'calendly.event_scheduled') return;
    var payload = data.payload || {};
    var uri = String((payload.invitee && payload.invitee.uri) || (payload.event && payload.event.uri) || '');
    track('calendly', uri.split('/').pop().slice(0, 60));
  });

  return { track: track };
})();
